<template>
  <nav
    class="ui-side-nav"
    :class="[
      surface && 'ui-side-nav--surface',
      orientation !== 'vertical' && `ui-side-nav--${orientation}`,
      `ui-side-nav--${density}`
    ]"
    :aria-label="ariaLabel"
  >
    <header
      v-if="heading || showCount"
      class="ui-side-nav__header"
    >
      <span v-if="heading">{{ heading }}</span>
      <span v-if="showCount">{{ items.length }}</span>
    </header>

    <div class="ui-side-nav__list">
      <component
        :is="componentForItem(item)"
        v-for="item in items"
        :key="itemKey(item)"
        v-bind="attrsForItem(item)"
        :class="[
          'ui-side-nav__link',
          isActive(item) && 'ui-side-nav__link--active',
          item.disabled && 'ui-side-nav__link--disabled'
        ]"
        :aria-current="isActive(item) ? 'page' : undefined"
        @click="handleItemClick(item, $event)"
        @dragstart="handleItemDragStart(item, $event)"
        @dragend="handleItemDragEnd(item, $event)"
      >
        <span
          v-if="item.icon || $slots['item-icon']"
          class="ui-side-nav__icon"
          aria-hidden="true"
        >
          <slot
            name="item-icon"
            :item="item"
            :active="isActive(item)"
          >
            <component
              :is="item.icon"
              v-if="isComponentIcon(item.icon)"
              :size="iconSize"
              :stroke-width="iconStrokeWidth"
            />
            <img
              v-else-if="isImageIcon(item.icon)"
              :src="item.icon"
              alt=""
            >
            <span
              v-else-if="item.icon"
              class="ui-side-nav__icon-text"
            >
              {{ item.icon }}
            </span>
          </slot>

          <span
            v-if="isActive(item)"
            class="ui-side-nav__marker"
          />
        </span>

        <span class="ui-side-nav__content">
          <span class="ui-side-nav__label">
            <slot
              name="item-label"
              :item="item"
              :active="isActive(item)"
            >
              <UiTruncatedText
                :text="item.label"
                :lines="1"
              />
            </slot>
          </span>
          <UiTruncatedText
            v-if="item.description"
            as="span"
            class="ui-side-nav__description"
            :text="item.description"
            :lines="1"
          />
        </span>

        <UiBadge
          v-if="item.badge !== undefined"
          class="ui-side-nav__badge"
          :tone="item.badgeTone ?? 'info'"
        >
          {{ item.badge }}
        </UiBadge>
      </component>
    </div>

    <footer
      v-if="$slots.footer"
      class="ui-side-nav__footer"
    >
      <slot name="footer" />
    </footer>
  </nav>
</template>

<script lang="ts">
import type { Component } from 'vue';

export type UiSideNavBadgeTone = 'default' | 'muted' | 'success' | 'warning' | 'danger' | 'info';
export type UiSideNavIcon = string | Component;

export type UiSideNavItem = {
  to?: string;
  href?: string;
  label: string;
  description?: string;
  icon?: UiSideNavIcon;
  badge?: string | number;
  badgeTone?: UiSideNavBadgeTone;
  disabled?: boolean;
  isActive?: boolean;
  active?: boolean;
  target?: string;
  rel?: string;
  draggable?: boolean;
  onClick?: (event: MouseEvent) => void;
  onDragStart?: (event: DragEvent) => void;
  onDragEnd?: (event: DragEvent) => void;
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

import UiBadge from './UiBadge.vue';
import UiTruncatedText from './UiTruncatedText.vue';

type Orientation = 'vertical' | 'horizontal' | 'grid';
type Density = 'comfortable' | 'compact';

const props = withDefaults(
  defineProps<{
    items: UiSideNavItem[];
    heading?: string;
    ariaLabel?: string;
    orientation?: Orientation;
    density?: Density;
    surface?: boolean;
    showCount?: boolean;
    iconSize?: number | string;
    iconStrokeWidth?: number | string;
    isNavLinkActive?: (to: string) => boolean;
  }>(),
  {
    heading: undefined,
    ariaLabel: 'Основная навигация',
    orientation: 'vertical',
    density: 'comfortable',
    surface: false,
    showCount: false,
    iconSize: 20,
    iconStrokeWidth: 1.9,
    isNavLinkActive: undefined
  }
);

const currentPath = computed(() => {
  const location = (globalThis as { location?: { pathname?: string } } | undefined)?.location;
  return location?.pathname ?? '';
});

const isComponentIcon = (icon?: UiSideNavIcon): icon is Component =>
  typeof icon === 'function' || (typeof icon === 'object' && icon !== null);

const isImageIcon = (icon?: UiSideNavIcon): icon is string => {
  if (typeof icon !== 'string') return false;
  return (
    icon.startsWith('/') ||
    icon.startsWith('./') ||
    icon.startsWith('../') ||
    icon.startsWith('http') ||
    icon.startsWith('data:') ||
    /\.(?:svg|png|jpe?g|webp|gif)$/i.test(icon)
  );
};

const itemKey = (item: UiSideNavItem) => item.to ?? item.href ?? item.label;

const isActive = (item: UiSideNavItem) => {
  const explicit = item.isActive ?? item.active;
  if (explicit !== undefined) return explicit;
  if (!item.to) return false;
  const fromCallback = props.isNavLinkActive?.(item.to);
  if (fromCallback !== undefined) return fromCallback;
  return currentPath.value === item.to || currentPath.value.startsWith(`${item.to}/`);
};

const componentForItem = (item: UiSideNavItem) => {
  if (item.disabled) return 'span';
  if (item.href) return 'a';
  if (!item.to) return 'button';
  return RouterLink;
};

const attrsForItem = (item: UiSideNavItem) => {
  if (item.disabled) {
    return { 'aria-disabled': 'true' };
  }
  if (item.href) {
    return {
      href: item.href,
      target: item.target,
      rel: item.rel,
      draggable: item.draggable || undefined
    };
  }
  if (item.to) {
    return {
      to: item.to,
      draggable: item.draggable || undefined
    };
  }
  return {
    type: 'button',
    draggable: item.draggable || undefined
  };
};

const handleItemClick = (item: UiSideNavItem, event: MouseEvent) => {
  if (item.disabled) {
    event.preventDefault();
    return;
  }
  item.onClick?.(event);
};

const handleItemDragStart = (item: UiSideNavItem, event: DragEvent) => {
  if (item.disabled || !item.draggable) {
    event.preventDefault();
    return;
  }
  item.onDragStart?.(event);
};

const handleItemDragEnd = (item: UiSideNavItem, event: DragEvent) => {
  if (item.disabled || !item.draggable) {
    return;
  }
  item.onDragEnd?.(event);
};
</script>

<style scoped>
.ui-side-nav {
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
}

.ui-side-nav--surface {
  width: 16rem;
  max-width: 100%;
  min-height: 31.5625rem;
  padding: 1.375rem 1rem 1.1875rem;
  border-radius: var(--ui-radius-xl);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-panel);
}

.ui-side-nav--horizontal,
.ui-side-nav--grid {
  width: 100%;
}

.ui-side-nav__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--ui-foreground-quiet);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  line-height: 0.875rem;
  padding: 0 0.375rem 1.3125rem;
  text-transform: uppercase;
}

.ui-side-nav__header span:last-child {
  color: var(--ui-accent);
  font-size: 0.8125rem;
  letter-spacing: 0;
  text-transform: none;
}

.ui-side-nav__list {
  display: flex;
  flex-direction: column;
  gap: 1.0625rem;
}

.ui-side-nav--compact .ui-side-nav__list {
  gap: var(--ui-space-1);
}

.ui-side-nav--horizontal .ui-side-nav__list {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
}

.ui-side-nav--grid .ui-side-nav__list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12.5rem, 1fr));
}

.ui-side-nav__link {
  position: relative;
  display: flex;
  width: 100%;
  min-width: 0;
  min-height: 2.875rem;
  appearance: none;
  align-items: center;
  gap: var(--ui-space-4);
  border: 0;
  border-radius: var(--ui-radius-sm);
  background: transparent;
  color: var(--ui-foreground-muted);
  cursor: pointer;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  padding: 0.625rem 1.1875rem;
  text-align: left;
  text-decoration: none;
  transition:
    background-color 0.16s ease,
    color 0.16s ease,
    transform 0.16s ease;
}

.ui-side-nav--compact .ui-side-nav__link {
  gap: var(--ui-space-3);
  min-height: 2.5rem;
  padding: 0.5rem 0.75rem;
}

.ui-side-nav__link:hover {
  background: color-mix(in srgb, var(--ui-accent) 10%, var(--ui-surface));
  color: var(--ui-accent);
  transform: translateY(-1px);
}

.ui-side-nav__link:active {
  transform: translateY(0);
}

.ui-side-nav__link--active {
  background: color-mix(in srgb, var(--ui-accent) 12%, var(--ui-surface));
  color: var(--ui-accent);
  font-weight: 600;
}

.ui-side-nav__link--disabled {
  cursor: not-allowed;
  opacity: 0.58;
  transform: none;
}

.ui-side-nav__icon {
  position: relative;
  display: inline-flex;
  width: 1.25rem;
  height: 1.25rem;
  flex: 0 0 1.25rem;
  align-items: center;
  justify-content: center;
  color: currentColor;
}

.ui-side-nav__icon img,
.ui-side-nav__icon :deep(svg) {
  display: block;
  width: 1.25rem;
  height: 1.25rem;
}

.ui-side-nav__icon-text {
  font-size: 1rem;
  line-height: 1;
}

.ui-side-nav__marker {
  position: absolute;
  right: -0.25rem;
  bottom: -0.25rem;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: var(--ui-radius-full);
  background: var(--ui-info);
}

.ui-side-nav__content {
  display: grid;
  min-width: 0;
  flex: 1;
  gap: 0.125rem;
}

.ui-side-nav__label,
.ui-side-nav__description {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ui-side-nav__description {
  color: var(--ui-foreground-quiet);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.15rem;
}

.ui-side-nav__badge {
  flex: 0 0 auto;
  min-width: 1.75rem;
}

.ui-side-nav__footer {
  margin-top: var(--ui-space-4);
  color: var(--ui-foreground-muted);
  font-size: 0.8125rem;
  line-height: 1.4;
}
</style>
